import { z } from "zod";
import { fulfillmentProviderEnvironmentSchema, fulfillmentSha256Schema, fulfillmentPrintSourceIdSchema, } from "./fulfillment.js";
import { fulfillmentRuntimeSchemaVersionSchema, physicalOrderSourceKindSchema, } from "./fulfillment-runtime.js";
import { ulidSchema } from "./schemas.js";
const timestampSchema = z.string().datetime({ offset: true });
const dateSchema = z.string().date();
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
const opaqueReferenceSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:#/-]*$/u);
const moneySchema = z
    .object({ currency: z.literal("USD"), amountMinor: z.number().int().nonnegative() })
    .strict();
/**
 * Durable runtime approval. Absence is interpreted as DISABLED; environment
 * flags alone can never authorize a provider mutation.
 */
export const fulfillmentRuntimeControlSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    state: z.enum(["DISABLED", "SANDBOX_ONLY", "PRODUCTION_APPROVED"]),
    revision: z.number().int().nonnegative(),
    approvedEnvironment: fulfillmentProviderEnvironmentSchema.optional(),
    approvalEvidenceDigestSha256: fulfillmentSha256Schema.optional(),
    activationScope: z
        .discriminatedUnion("phase", [
        z
            .object({
            phase: z.literal("PREPARE_ONE_BOOK"),
            ownerUserId: ulidSchema,
            bookId: ulidSchema,
            previewId: fulfillmentPrintSourceIdSchema,
            expectedRevision: z.number().int().nonnegative(),
            expiresAt: timestampSchema,
            maximumPhysicalOrders: z.literal(1),
        })
            .strict(),
        z
            .object({
            phase: z.literal("BEDTIME_END_TO_END"),
            ownerUserId: ulidSchema,
            bookId: ulidSchema,
            previewId: fulfillmentPrintSourceIdSchema,
            expectedRevision: z.number().int().nonnegative(),
            expiresAt: timestampSchema,
            maximumPhysicalOrders: z.literal(1),
        })
            .strict(),
        z
            .object({
            phase: z.literal("SUBMIT_ONE_ORDER"),
            ownerUserId: ulidSchema,
            bookId: ulidSchema,
            previewId: fulfillmentPrintSourceIdSchema,
            expectedRevision: z.number().int().nonnegative(),
            physicalOrderId: ulidSchema,
            expiresAt: timestampSchema,
            maximumPhysicalOrders: z.literal(1),
        })
            .strict(),
        /**
         * Owner decision 6 (2026-10-15): real printing is open to every customer's Bedtime book.
         * No book, owner or expiry binds it; each order is still bound to its book's one included
         * printed copy and to the global print cost policy. Production only: a Sandbox order
         * would use up a real customer's one printed copy on a book that is never printed.
         */
        z
            .object({
            phase: z.literal("CUSTOMER_PRINTING"),
            openedAt: timestampSchema,
        })
            .strict(),
        /**
         * Closed to new orders: orders already placed keep being tracked (status, emails, order
         * pages); no new order, package, quote or cancellation reaches the printer.
         */
        z
            .object({
            phase: z.literal("CUSTOMER_PRINTING_DRAINING"),
            openedAt: timestampSchema,
            closedToNewOrdersAt: timestampSchema,
        })
            .strict(),
    ])
        .optional(),
    updatedAt: timestampSchema,
})
    .strict()
    .superRefine((control, context) => {
    const expected = control.state === "SANDBOX_ONLY"
        ? "SANDBOX"
        : control.state === "PRODUCTION_APPROVED"
            ? "PRODUCTION"
            : undefined;
    if (control.approvedEnvironment !== expected)
        context.addIssue({
            code: "custom",
            path: ["approvedEnvironment"],
            message: "Runtime approval must bind exactly one provider environment.",
        });
    if ((control.state !== "DISABLED") !== Boolean(control.approvalEvidenceDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["approvalEvidenceDigestSha256"],
            message: "Enabled runtime control requires immutable approval evidence.",
        });
    if (control.state === "DISABLED" && control.activationScope)
        context.addIssue({
            code: "custom",
            path: ["activationScope"],
            message: "Disabled runtime control cannot retain an activation scope.",
        });
    if ((control.activationScope?.phase === "CUSTOMER_PRINTING" ||
        control.activationScope?.phase === "CUSTOMER_PRINTING_DRAINING") &&
        control.state !== "PRODUCTION_APPROVED")
        context.addIssue({
            code: "custom",
            path: ["activationScope", "phase"],
            message: "Printing open to customers is approved for production only.",
        });
});
export const fulfillmentGlobalCostPolicySchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    policyId: opaqueReferenceSchema,
    maxPerOrderMinor: z.number().int().positive(),
    maxDailyMinor: z.number().int().positive(),
    maxMonthlyMinor: z.number().int().positive(),
    maxProfileDailyMinor: z.number().int().positive(),
    maxProfileMonthlyMinor: z.number().int().positive(),
    maxSourceDailyMinor: z.number().int().positive(),
    maxSourceMonthlyMinor: z.number().int().positive(),
    maxActualQuoteVarianceMinor: z.number().int().nonnegative(),
    maxActualQuoteVarianceBasisPoints: z.number().int().min(0).max(10_000),
    maxArtifactBytes: z.number().int().positive().max(2_000_000_000),
    maxArtifactDownloadAttempts: z.number().int().min(1).max(10),
    smsMaxPerUserDaily: z.number().int().min(0).max(20),
    smsMaxDailyMinor: z.number().int().nonnegative(),
    smsMaxMonthlyMinor: z.number().int().nonnegative(),
})
    .strict();
export const fulfillmentCostReservationSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    profileId: opaqueReferenceSchema,
    sourceKind: physicalOrderSourceKindSchema,
    providerEnvironment: fulfillmentProviderEnvironmentSchema,
    quoted: moneySchema,
    state: z.enum(["RESERVED", "SETTLED", "RELEASED"]),
    day: dateSchema,
    month: z.string().regex(/^\d{4}-\d{2}$/u),
    reservedAt: timestampSchema,
    settledAt: timestampSchema.optional(),
    releasedAt: timestampSchema.optional(),
    actual: moneySchema.optional(),
    financialStatus: safeCodeSchema.optional(),
    evidenceDigestSha256: fulfillmentSha256Schema.optional(),
})
    .strict()
    .superRefine((reservation, context) => {
    if (reservation.state === "RESERVED" && (reservation.actual || reservation.settledAt))
        context.addIssue({ code: "custom", path: ["state"], message: "Reserved cost is unsettled." });
    if (reservation.state === "SETTLED" && (!reservation.actual || !reservation.settledAt))
        context.addIssue({
            code: "custom",
            path: ["actual"],
            message: "Settled cost requires actual provider evidence.",
        });
    if (reservation.state === "RELEASED" && !reservation.releasedAt)
        context.addIssue({
            code: "custom",
            path: ["releasedAt"],
            message: "Released cost requires a release time.",
        });
});
export const providerFinancialEvidenceSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    providerEnvironment: fulfillmentProviderEnvironmentSchema,
    financialStatus: z.enum([
        "DRAFT",
        "PENDING",
        "INVOICED",
        "TO_BE_INVOICED",
        "PAID",
        "CANCELED",
        "PARTIALLY_REFUNDED",
        "REFUNDED",
        "REFUSED",
        "UNKNOWN",
    ]),
    quoted: moneySchema,
    actual: moneySchema,
    tax: moneySchema,
    varianceMinor: z.number().int(),
    providerReceiptDigestSha256: fulfillmentSha256Schema,
    observedAt: timestampSchema,
})
    .strict();
const evidenceDimensionSchema = z
    .object({ value: z.number(), measureUnit: z.string().trim().min(1).max(30) })
    .strict();
export const gelatoCatalogEvidenceSnapshotSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    snapshotId: ulidSchema,
    providerEnvironment: fulfillmentProviderEnvironmentSchema,
    profileId: opaqueReferenceSchema,
    profileVersion: opaqueReferenceSchema,
    vendorProductUidDigestSha256: fulfillmentSha256Schema,
    supportedCountries: z.array(z.string().length(2)).max(250),
    validPageCounts: z.array(z.number().int().positive()).max(2_000),
    coverDimensions: z.record(z.string(), evidenceDimensionSchema),
    productPriceDigestSha256: fulfillmentSha256Schema,
    shippingPriceDigestSha256: fulfillmentSha256Schema,
    stockDigestSha256: fulfillmentSha256Schema,
    catalogDigestSha256: fulfillmentSha256Schema,
    capturedAt: timestampSchema,
})
    .strict();
export const gelatoCatalogEvidenceDiffSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    diffId: ulidSchema,
    profileId: opaqueReferenceSchema,
    profileVersion: opaqueReferenceSchema,
    previousSnapshotId: ulidSchema.optional(),
    currentSnapshotId: ulidSchema,
    safeChangeCodes: z.array(safeCodeSchema).max(30),
    quarantined: z.boolean(),
    comparedAt: timestampSchema,
})
    .strict()
    .superRefine((diff, context) => {
    if (diff.quarantined !== diff.safeChangeCodes.length > 0)
        context.addIssue({
            code: "custom",
            path: ["quarantined"],
            message: "Any catalog drift must quarantine the product profile.",
        });
});
const webhookBase = {
    id: opaqueReferenceSchema,
    orderId: opaqueReferenceSchema,
    orderReferenceId: opaqueReferenceSchema,
    storeId: opaqueReferenceSchema.nullable().optional(),
};
export const gelatoWebhookEventSchemaV1 = z.discriminatedUnion("event", [
    z
        .object({
        ...webhookBase,
        event: z.literal("order_status_updated"),
        fulfillmentStatus: z.string().trim().min(1).max(80),
        items: z.array(z.unknown()).max(500),
        comment: z.string().max(2_000).optional(),
    })
        .passthrough(),
    z
        .object({
        ...webhookBase,
        event: z.literal("order_item_status_updated"),
        itemReferenceId: opaqueReferenceSchema,
        status: z.string().trim().min(1).max(80),
        created: timestampSchema,
    })
        .passthrough(),
    z
        .object({
        ...webhookBase,
        event: z.literal("order_item_tracking_code_updated"),
        itemReferenceId: opaqueReferenceSchema,
        trackingCode: z.string().trim().min(1).max(200),
        trackingUrl: z.string().url(),
        shipmentMethodName: z.string().trim().min(1).max(200),
        shipmentMethodUid: opaqueReferenceSchema,
        created: timestampSchema,
    })
        .passthrough(),
    z
        .object({
        ...webhookBase,
        event: z.literal("order_delivery_estimate_updated"),
        minDeliveryDate: dateSchema,
        maxDeliveryDate: dateSchema,
        created: timestampSchema,
    })
        .passthrough()
        .superRefine((event, context) => {
        if (event.maxDeliveryDate < event.minDeliveryDate)
            context.addIssue({
                code: "custom",
                path: ["maxDeliveryDate"],
                message: "Maximum delivery date cannot precede minimum delivery date.",
            });
    }),
]);
export const fulfillmentNotificationFanoutSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    notificationId: ulidSchema,
    recipientUserId: ulidSchema,
    physicalOrderId: ulidSchema,
    type: z.enum([
        "ORDER_RECEIVED",
        "ACCEPTED",
        "PARTIALLY_SHIPPED",
        "SHIPPED",
        "DELIVERED",
        "RETURNED",
        "NEEDS_ATTENTION",
        "TRACKING_AVAILABLE",
        "DELIVERY_DELAYED",
        "ADDRESS_ACTION_REQUIRED",
        "CANCELLATION_RESULT",
    ]),
    contentFree: z.literal(true),
    verifiedByReconciliation: z.literal(true),
    createdAt: timestampSchema,
    /**
     * When the problem this notification reports began (the order's attention record), so the
     * team is told once per incident, not once per order (review, 2026-10-15).
     */
    incidentAt: timestampSchema.optional(),
    /**
     * Which problem it is (the order's attention code). The team is told once per problem: the
     * attention record's time is refreshed by routine checks with the printer, so it can't tell
     * one incident from the next (review, 2026-10-15). Preferred over incidentAt when both come.
     */
    incidentCode: safeCodeSchema.optional(),
    /** Additive consumer-first rollout: exact committed producer identity, not current-state relabeling. */
    fulfillmentId: ulidSchema.optional(),
    committedVersion: z.number().int().nonnegative().optional(),
    transitionDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    deliveryEstimateDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
})
    .strict();
//# sourceMappingURL=fulfillment-readiness.js.map